import maplibrelgl from 'maplibre-gl';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  COMPS_URL,
  LISTINGS_URL,
  SAVED_LAND_PLAT_MAP_URL,
} from '@/constants/urls';
import { useInsightsCatalogContext } from '@/features/insightsCatalog/provider';
import { useLayerLibraryContext } from '@/features/layerLibrary/provider';
import { ToolNoneDrawModes } from '@/features/toolbar/constants';
import { useToolbarContext } from '@/features/toolbar/provider';
import { useDoesPathInclude } from '@/hooks/useDoesPathInclude';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';

import { boundaryFillLayer } from '../../mapbox-layers/boundary';
import { fsaFillLayer, fsaLabelsLayer } from '../../mapbox-layers/clu';
import { countyFillLayer } from '../../mapbox-layers/county/county';
import { countyOutlineLayer } from '../../mapbox-layers/county/county-outline';
import drawFillLayer from '../../mapbox-layers/draw';
import {
  parcelFillLayer,
  visibleOwnerLabelsLayer,
} from '../../mapbox-layers/parcels/parcels';
import { stateFillLayer, stateOutlineLayer } from '../../mapbox-layers/states';
import {
  sectionOutlineLayer,
  sectionsFillLayer,
} from '../../mapbox-layers/trs/sections';
import { townshipOutlineLayer } from '../../mapbox-layers/trs/township-outline';
import { townshipFillLayer } from '../../mapbox-layers/trs/townships';
import { RootState } from '../../redux/store';
import { getAllFeatureIds } from '../../utils/feature';
import useLayerHover from './useLayerHover';

const supportedToolModes = [
  ToolNoneDrawModes.Info_Tool,
  ToolNoneDrawModes.Quick_Tool,
];

/**
 * Controls hovers of different layers on the Map
 * @param map
 */
const useMapFeatureHover = (map: maplibrelgl.Map | undefined) => {
  const { allFeatures } = useSelector((state: RootState) => state.features);
  const { isUsingQuickMeasure } = useMapboxContext();
  const allFeatureIds = useMemo(
    () => getAllFeatureIds(allFeatures),
    [allFeatures]
  );
  const pathname = usePathname();

  const isOnSavedMaps = useDoesPathInclude(SAVED_LAND_PLAT_MAP_URL);

  const { selectedTool } = useToolbarContext();
  const { parcelsLayerDisabled } = useInsightsCatalogContext();
  const { parcelsLayerDisabled: llParcelsLayerDisabled } =
    useLayerLibraryContext();

  const isSelectedToolSupported = useMemo(() => {
    if (
      selectedTool.mode &&
      Object.values(supportedToolModes).includes(
        selectedTool.mode as ToolNoneDrawModes
      )
    )
      return true;

    return false;
  }, [selectedTool]);

  const parcelsShowHover: boolean =
    !parcelsLayerDisabled &&
    !llParcelsLayerDisabled &&
    !isOnSavedMaps &&
    !isUsingQuickMeasure &&
    isSelectedToolSupported;

  const fsaShowHover: boolean = parcelsShowHover;

  const isOnSoldLandPage = useMemo(
    () => pathname?.includes(COMPS_URL) && isSelectedToolSupported,
    [isSelectedToolSupported, pathname]
  );

  const isOnSavedMapsPage = useMemo(
    () =>
      pathname?.includes(SAVED_LAND_PLAT_MAP_URL) && isSelectedToolSupported,
    [isSelectedToolSupported, pathname]
  );

  const isOnListingsPage = useMemo(
    () => pathname?.includes(LISTINGS_URL) && isSelectedToolSupported,
    [isSelectedToolSupported, pathname]
  );

  const disableStateHover: boolean =
    isUsingQuickMeasure ||
    isOnSoldLandPage ||
    isOnSavedMapsPage ||
    isOnListingsPage ||
    !isSelectedToolSupported;

  const disableCountyHover: boolean = disableStateHover;
  const disableTownshipHover: boolean = disableStateHover;
  const disableSectionHover: boolean = disableStateHover;

  // Hover hooks
  useLayerHover(
    map,
    parcelFillLayer,
    parcelFillLayer,
    allFeatureIds,
    false,
    parcelsShowHover
  );
  useLayerHover(
    map,
    parcelFillLayer,
    visibleOwnerLabelsLayer,
    allFeatureIds,
    false,
    parcelsShowHover
  );
  useLayerHover(
    map,
    drawFillLayer,
    drawFillLayer,
    undefined,
    false,
    !isUsingQuickMeasure
  );
  useLayerHover(
    map,
    stateFillLayer,
    stateOutlineLayer,
    undefined,
    false,
    !disableStateHover
  );
  useLayerHover(
    map,
    countyFillLayer,
    countyOutlineLayer,
    undefined,
    false,
    !disableCountyHover
  );
  useLayerHover(
    map,
    townshipFillLayer,
    townshipOutlineLayer,
    undefined,
    false,
    !disableTownshipHover
  );
  useLayerHover(
    map,
    sectionsFillLayer,
    sectionOutlineLayer,
    undefined,
    false,
    !disableSectionHover
  );
  useLayerHover(
    map,
    fsaFillLayer,
    fsaFillLayer,
    undefined,
    false,
    fsaShowHover
  );
  useLayerHover(map, fsaFillLayer, fsaLabelsLayer);
  useLayerHover(
    map,
    boundaryFillLayer,
    boundaryFillLayer,
    undefined,
    false,
    !isUsingQuickMeasure
  );
};

export default useMapFeatureHover;
